"use client";

import React, { useEffect } from "react";
import { NavigationIcon } from "../Icon/NavigationIcon";
import { SideNav, type SideNavProps } from "../SideNav/SideNav";
import { StackNav, type StackNavProps } from "../StackNav/StackNav";
import { TopNav, type TopNavProps } from "../TopNav/TopNav";

/** Layout options for the AppShell */
export type AppShellProps = {
  /**
   * Structured nav for the left rail. Use one of `sideNav` or `stackNav`.
   * When both are provided, `stackNav` takes precedence and `sideNav` is ignored.
   */
  sideNav?: Omit<SideNavProps, "mobileMenuId" | "sidebarCollapseId">;
  /**
   * Use the StackNav primitive in the left rail instead of SideNav.
   */
  stackNav?: Omit<StackNavProps, "mobileMenuId" | "sidebarCollapseId">;
  /** Optional top bar slots & options */
  topNav?: Pick<
    TopNavProps,
    | "rightContent"
    | "centerContent"
    | "showMobileMenu"
    | "avatar"
    | "showColorModeSwitcher"
    | "toggleTheme"
    | "isDarkThemeEnabled"
    | "colorMode"
    | "actions"
    | "className"
  >;
  /** Main page content */
  children: React.ReactNode;

  /** Layout knobs */
  contentPaddingX?: string;
  contentPaddingY?: string;
  showSidebarBorder?: boolean;
  mainClassName?: string;

  /** Optional: Enable JavaScript progressive enhancements */
  enableJsEnhancements?: boolean;
};

export function AppShell({
  sideNav,
  stackNav,
  topNav,
  children,
  contentPaddingX,
  contentPaddingY,
  showSidebarBorder = true,
  mainClassName,
  enableJsEnhancements = false,
}: AppShellProps) {
  const mobileMenuId = "app-mobile-menu";
  const sidebarCollapseId = "app-sidebar-collapse";

  useEffect(() => {
    if (!enableJsEnhancements) return;

    const sidebarToggle = document.getElementById(sidebarCollapseId) as HTMLInputElement | null;
    const mobileToggle = document.getElementById(mobileMenuId) as HTMLInputElement | null;
    const shell = document.querySelector<HTMLElement>("[data-app-shell]");
    const sidebar = document.getElementById("app-sidebar");

    if (!sidebarToggle || !shell || !sidebar) return;

    let peekTimer: ReturnType<typeof setTimeout> | null = null;
    // When the user clicks the in-rail collapse button, the cursor is still
    // over the rail — without this guard, the peek mousemove handler would
    // immediately re-open the rail. The guard suppresses peek until the
    // cursor actually leaves the rail area.
    let suppressPeekUntilLeave = false;

    // localStorage can throw in Safari private mode and when storage quota is
    // exceeded. Guard each access so a failure here doesn't prevent listener
    // registration below.
    try {
      const saved = localStorage.getItem("sidebar-collapsed");
      if (saved !== null) sidebarToggle.checked = saved === "true";
    } catch {
      // Persisted state unavailable — fall back to default.
    }

    const handleCollapseChange = () => {
      try {
        localStorage.setItem("sidebar-collapsed", String(sidebarToggle.checked));
      } catch {
        // Persistence unavailable — collapse still works, just won't survive reloads.
      }
      if (sidebarToggle.checked) {
        // Just collapsed — arm the guard so peek can't immediately re-fire.
        suppressPeekUntilLeave = true;
      } else {
        // Expanded — drop peek state.
        if (peekTimer) {
          clearTimeout(peekTimer);
          peekTimer = null;
        }
        shell.removeAttribute("data-peek");
        suppressPeekUntilLeave = false;
      }
    };
    sidebarToggle.addEventListener("change", handleCollapseChange);

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (mobileToggle) mobileToggle.checked = false;
        shell.removeAttribute("data-peek");
      }
    };
    document.addEventListener("keydown", handleKeydown);

    let handleMobileChange: (() => void) | null = null;
    if (mobileToggle) {
      handleMobileChange = () => {
        document.body.style.overflow = mobileToggle.checked ? "hidden" : "";
      };
      mobileToggle.addEventListener("change", handleMobileChange);
    }

    const handleSidebarClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (link && link.href && window.innerWidth < 768 && mobileToggle) {
        setTimeout(() => {
          mobileToggle.checked = false;
          document.body.style.overflow = "";
        }, 100);
      }
    };
    sidebar.addEventListener("click", handleSidebarClick);

    // Hide-and-peek: while collapsed, moving the cursor within 16px of the
    // left edge reveals the rail as an overlay; leaving the rail starts a
    // 200ms grace timer before sliding it back out.
    const TRIGGER_PX = 16;
    const GRACE_MS = 200;
    const peekOn = () => {
      if (peekTimer) {
        clearTimeout(peekTimer);
        peekTimer = null;
      }
      if (shell.getAttribute("data-peek") !== "true") {
        shell.setAttribute("data-peek", "true");
      }
    };
    const schedulePeekOff = () => {
      if (peekTimer) clearTimeout(peekTimer);
      peekTimer = setTimeout(() => {
        shell.removeAttribute("data-peek");
        peekTimer = null;
      }, GRACE_MS);
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (window.innerWidth < 768) return;
      if (!sidebarToggle.checked) return;
      const inRail = sidebar.contains(e.target as Node);
      // Disarm the "just collapsed" guard once the cursor leaves the rail area.
      if (suppressPeekUntilLeave && !inRail) {
        suppressPeekUntilLeave = false;
      }
      if (suppressPeekUntilLeave) return;
      const inTrigger = e.clientX <= TRIGGER_PX;
      if (inTrigger || inRail) {
        peekOn();
      } else if (shell.getAttribute("data-peek") === "true") {
        schedulePeekOff();
      }
    };
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      sidebarToggle.removeEventListener("change", handleCollapseChange);
      document.removeEventListener("keydown", handleKeydown);
      if (mobileToggle && handleMobileChange) {
        mobileToggle.removeEventListener("change", handleMobileChange);
      }
      sidebar.removeEventListener("click", handleSidebarClick);
      document.removeEventListener("mousemove", handleMouseMove);
      if (peekTimer) clearTimeout(peekTimer);
    };
  }, [enableJsEnhancements, mobileMenuId, sidebarCollapseId]);

  return (
    <div className="min-h-[100dvh] bg-background text-text-primary relative" data-app-shell="">
      {/* Hidden checkbox for desktop sidebar collapse */}
      <input type="checkbox" id={sidebarCollapseId} className="sr-only peer/sidebar" aria-hidden="true" />

      {/* Skip link for keyboard users */}
      <a
        href="#app-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 z-50 rounded px-3 py-2 bg-background-surface shadow"
      >
        Skip to content
      </a>

      {/* Top navigation - always visible on mobile */}
      <TopNav
        {...topNav}
        mobileMenuId={mobileMenuId}
        className={["sticky top-0 flex-shrink-0 z-30 md:hidden", topNav?.className].filter(Boolean).join(" ")}
      />

      {/* Main layout container */}
      <div className="flex w-full h-[calc(100dvh-60px)] md:h-[100dvh] relative overflow-hidden">
        {/* Hidden checkbox for mobile menu */}
        <input type="checkbox" id={mobileMenuId} className="sr-only peer/mobile" aria-hidden="true" />

        {/* Mobile menu overlay */}
        <label
          htmlFor={mobileMenuId}
          className="fixed inset-0 bg-background-modal z-[55] opacity-0 pointer-events-none peer-checked/mobile:opacity-100 peer-checked/mobile:pointer-events-auto transition-opacity md:hidden"
          aria-label="Close menu"
        />

        {/* Desktop peek hover-zone (16px on the left edge, only when collapsed) */}
        <div
          aria-hidden="true"
          data-peek-zone=""
          className="hidden md:block fixed left-0 top-0 bottom-0 w-4 z-[70] pointer-events-none"
        />

        {/* Floating expand button — only visible on desktop when the rail is collapsed */}
        <label
          htmlFor={sidebarCollapseId}
          data-expand-rail=""
          className="hidden fixed left-3 top-3 z-[75] items-center justify-center w-9 h-9 rounded-md bg-background-surface border border-border-default shadow-sm hover:bg-background-hover cursor-pointer transition-all duration-200"
          aria-label="Expand sidebar"
        >
          <NavigationIcon name="Sidebar" />
        </label>

        {/* Left rail */}
        <aside
          id="app-sidebar"
          data-app-sidebar=""
          className={[
            "bg-background-surface",
            "border-r border-border-default",
            "transition-transform duration-300 ease-in-out md:transition-[transform,width] md:duration-300 md:ease-in-out",
            "fixed md:relative",
            "left-0 top-0 bottom-0",
            "z-[60] md:z-10",
            "w-full md:w-64",
            "h-[100dvh] md:h-full",
            "-translate-x-full",
            "peer-checked/mobile:translate-x-0",
            "md:translate-x-0",
            showSidebarBorder ? "" : "!border-r-0",
            (stackNav ?? sideNav)?.className || "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {stackNav ? (
            <StackNav
              {...stackNav}
              mobileMenuId={mobileMenuId}
              sidebarCollapseId={sidebarCollapseId}
              sticky={false}
              width={null}
              className={["h-full !border-r-0", stackNav.className].filter(Boolean).join(" ")}
            />
          ) : (
            <SideNav
              {...sideNav}
              mobileMenuId={mobileMenuId}
              sidebarCollapseId={sidebarCollapseId}
              className="h-full"
            />
          )}
        </aside>

        {/* Right side: TopNav + Main content */}
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Top navigation - desktop only */}
          <div className="sticky top-0 flex-shrink-0 hidden md:block z-20" data-top-nav-wrapper="">
            <TopNav {...topNav} className={topNav?.className} />
          </div>

          {/* Main content */}
          <main
            id="app-content"
            className={[
              "flex-1 overflow-y-auto overflow-x-hidden bg-background-body scrollbar-visible",
              contentPaddingX,
              contentPaddingY,
              mainClassName || "",
            ].join(" ")}
            role="main"
          >
            {children}
          </main>
        </div>
      </div>

      {/* CSS for collapse + peek behavior. The checkbox state drives collapse; a
        runtime-set [data-peek="true"] on the shell overrides it temporarily. */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (min-width: 768px) {
          /* Collapsed: hide the rail offscreen, surface the floating expand
             button, and let the edge zone receive hover events. */
          #${sidebarCollapseId}:checked ~ * [data-app-sidebar] {
            transform: translateX(-100%);
            position: absolute;
          }
          #${sidebarCollapseId}:checked ~ * [data-expand-rail] {
            display: flex;
          }
          #${sidebarCollapseId}:checked ~ * [data-top-nav-wrapper] {
            padding-left: 3.5rem;
          }
          #${sidebarCollapseId}:checked ~ * [data-peek-zone] {
            pointer-events: auto;
          }

          /* Peek: when the shell carries data-peek="true", show the rail as
             an overlay regardless of checkbox state. JS toggles the attribute
             based on edge-hover + rail-hover with a "just collapsed" guard. */
          [data-app-shell][data-peek="true"] [data-app-sidebar] {
            transform: translateX(0) !important;
            position: absolute !important;
            box-shadow: 0 8px 24px rgb(0 0 0 / 0.12);
            z-index: 50;
          }
        }
      `,
        }}
      />
    </div>
  );
}
